import { CdkDrag } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, booleanAttribute, inject, input } from '@angular/core';

/**
 * A floating window the user can drag around the viewport and resize from its bottom-right corner.
 * Position it and size it from the consumer's styles (`top`, `left`, `width`, `height` on the host);
 * dragging adds a transform on top of that, resizing sets the width and height.
 * Mark the element to grab with `simFloatingWindowHandle`; without a handle the whole window drags.
 */
@Component({
  selector: 'sim-floating-window',
  template: `<ng-content />`,
  styleUrl: './sim-floating-window.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: CdkDrag, inputs: ['cdkDragBoundary: boundary', 'cdkDragDisabled: dragDisabled'] }],
  host: {
    class: 'sim-floating-window',
    '[class.sim-floating-window--resizable]': 'resizable()'
  }
})
export class SimFloatingWindowComponent {
  private readonly _drag = inject(CdkDrag, { self: true });

  /** Shows the resize grip in the bottom-right corner; the consumer's `min-*`/`max-*` styles bound the size. */
  readonly resizable = input(true, { transform: booleanAttribute });

  constructor() {
    // Keep the window inside the viewport unless the consumer passes its own boundary.
    this._drag.boundaryElement = inject(DOCUMENT).documentElement;
  }

  /** Moves the window back to where its styles put it. */
  resetPosition(): void {
    this._drag.reset();
  }
}
